/**
 * ConversationRun page — /brand/conversations/:personaId/launches/:launchId/cells/:cellPath/runs/:idx
 *
 * The Run view of §4.5.4, laid out after docs/mocks/conversations/conversation.html
 * (which the mock README declares to be the spec). It replaces the right-hand drawer +
 * transcript dialog the matrix used to open: one run, one page, one scroll.
 *
 * Block order:
 *   1. Run picker (Zone · Moteur · Run + ‹ ›)
 *   2. « Ce run » — the verdict in one sentence, the outcome ladder in the configured
 *      intent's own vocabulary, the funnel strip (configured intent · observed intent
 *      turn by turn · presence by observed intent) and the five numbers
 *   4. « Marques citées dans ce run » · « Cibles citées dans ce run » · « Sources citées par X »
 *   5. « Tour par tour » — the signals × turns matrix
 *   6. « Lecture de la conversation »
 *
 * Block 2 was rebuilt after /tmp/traaker-run-page-mockups/03-atelier-analyste.html
 * (tier 1), the approved design. Block 3 (« Recommandés à la place ») is gone: the
 * brand that took the place is now named in block 2's lede, and block 4's brands ×
 * turns matrix already carries every brand's state at the drop-out turn.
 *
 * Three reads, all existing endpoints:
 *   …/launches/:launchId/matrix          — every cell + its `runs[]` (pickers, squares, comparison)
 *   …/cells/:cellPath/sources            — RUNX# extras (the cell's domains, for context)
 *   …/cells/:cellPath/conversations/:idx — this run's turns (everything per-run is derived here)
 *
 * Everything the page states about THIS run is derived from its turns, never from the
 * cell aggregate: the cell aggregates all its runs, and attributing that to one run
 * would be a false claim.
 *
 * Deviations from the mock, deliberate:
 *   - light MUI palette (the app has no dark theme); the phone transcript is light too
 *     since 2026-10-05 — the dark `CHAT` palette is gone, only the device bezel is dark
 *   - the annotations and « à la place » lines are derived from measured signals, not
 *     the mock's hand-authored prose; « Pourquoi X décroche » is omitted (mock README
 *     §6.9 — its data source is undecided, and the mock does not render it either)
 *   - no date/launch select: the page is scoped to the launch you arrived from
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Select,
  Skeleton,
  Snackbar,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DownloadIcon from '@mui/icons-material/Download';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CONVERSATION_INTENTS,
  cellPath as buildCellPath,
  parseCellPath,
  launchTs,
  type ConversationEngine,
  type ConversationPersona,
  type ConversationSubRun,
  type ConversationSubRunExtras,
  type ConversationTurn,
} from '@traaker/shared';
import { api, ApiError } from '../services/api';
import { useBrand } from '../contexts/BrandContext';
import { EngineLogo, ENGINE_META, engineKey } from '../components/brand/EngineLogo';
import { CATEGORY_CHIP_COLOR } from '../utils/targetColors';
import { ConversationTranscript } from '../components/conversations/ConversationTranscript';
import { RunSquare, squareDesc, squareKind } from '../components/conversations/RunSquare';
import { CompetitorFollowDialog } from '../components/conversations/CompetitorFollowDialog';
import DomainFavicon from '../components/competitors/DomainFavicon';

// ---------------------------------------------------------------------------
// Small shared pieces
// ---------------------------------------------------------------------------

const OBSERVED_INTENT_ORDER = ['learn', 'discover', 'compare', 'intra_compare', 'authority', 'transact', 'troubleshoot', 'other'] as const;
type ObservedIntentKey = (typeof OBSERVED_INTENT_ORDER)[number];

function Panel({ children, sx }: { children: React.ReactNode; sx?: object }) {
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2.5, bgcolor: 'background.paper', boxShadow: '0 1px 2px rgba(23,21,29,.05), 0 4px 16px rgba(23,21,29,.05)', ...sx }}>
      {children}
    </Box>
  );
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      fontWeight={700}
      sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.6875rem', display: 'block' }}
    >
      {children}
    </Typography>
  );
}

function SectionTitle({ children, hint }: { children: React.ReactNode; hint?: string }) {
  return (
    <Box sx={{ mb: 1.75 }}>
      <Typography fontWeight={700} fontSize="1.0625rem">{children}</Typography>
      {hint && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{hint}</Typography>}
    </Box>
  );
}

/** The mock's `<details>` — also the lazy-read boundary, so keep it collapsed by default. */
function Details({ summary, children }: { summary: string; children: React.ReactNode }) {
  return (
    <Box
      component="details"
      sx={{
        mt: 1.5,
        '& > summary': {
          cursor: 'pointer',
          fontSize: '0.8125rem',
          fontWeight: 600,
          color: 'primary.main',
          listStyle: 'none',
          '&::-webkit-details-marker': { display: 'none' },
          '&::before': { content: '"▸ "' },
        },
        '&[open] > summary::before': { content: '"▾ "' },
      }}
    >
      <Box component="summary">{summary}</Box>
      <Box sx={{ mt: 1.5 }}>{children}</Box>
    </Box>
  );
}

const CELL_SX = { borderBottom: '1px solid', borderColor: 'divider', px: 1, py: 0.75, fontSize: '0.8125rem', textAlign: 'left' as const };
const HEAD_SX = { ...CELL_SX, fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.06em', color: 'text.secondary', whiteSpace: 'nowrap' as const };

// ---------------------------------------------------------------------------
// Derivations — everything below is computed from THIS run's turns
// ---------------------------------------------------------------------------

function domainOf(url: string): string | null {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

function truncateUrl(url: string, max = 60): string {
  try {
    const u = new URL(url);
    const short = u.hostname.replace(/^www\./, '') + u.pathname + (u.search.length > 1 ? u.search : '');
    return short.length > max ? short.slice(0, max) + '…' : short;
  } catch {
    return url.length > max ? url.slice(0, max) + '…' : url;
  }
}

function formatDuration(ms: number): string {
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} s`;
  return `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, '0')} s`;
}

function pct(v: number | null | undefined): string | null {
  return v == null ? null : `${Math.round(v * 100)} %`;
}

/** First turn the brand is absent from after having been cited. */
function dropOutTurnOf(turns: ConversationTurn[]): number | null {
  let seen = false;
  for (const turn of turns) {
    if (turn.cited) { seen = true; continue; }
    if (seen) return turn.turn;
  }
  return null;
}

// ---------------------------------------------------------------------------
// « Intention observée » — the funnel axis
//
// The three CONFIGURED intents are funnel DEPTHS and `FUNNEL_LANES` lists them
// top of funnel FIRST, so a turn that moves back up the funnel draws a line going
// UP. This axis is NOT `stage`: `stage` buckets the turn number and can therefore
// never regress, `observedIntent` is measured per turn by the judge and can move
// either way. The two share no vocabulary and never share a column.
// ---------------------------------------------------------------------------

const FUNNEL_LANES = ['learn', 'discover', 'compare'] as const;
type FunnelLane = (typeof FUNNEL_LANES)[number];

const DEPTH_LABEL: Record<FunnelLane, string> = {
  learn: 'conversations.runV2.depthTop',
  discover: 'conversations.runV2.depthMid',
  compare: 'conversations.runV2.depthLow',
};
const DEPTH_SHORT: Record<FunnelLane, string> = {
  learn: 'conversations.runV2.depthShortTop',
  discover: 'conversations.runV2.depthShortMid',
  compare: 'conversations.runV2.depthShortLow',
};
const DEPTH_MEANING: Record<FunnelLane, string> = {
  learn: 'conversations.runV2.meaningLearn',
  discover: 'conversations.runV2.meaningDiscover',
  compare: 'conversations.runV2.meaningCompare',
};

function isFunnelLane(k: string): k is FunnelLane {
  return (FUNNEL_LANES as readonly string[]).includes(k);
}

/**
 * The judge's observed intent for one turn, or `null` when it has none. `null`
 * covers two honest unknowns the caller must keep apart: a failed turn never gets
 * one, and a completed turn may still be awaiting Layer B. Nothing is defaulted.
 */
function observedIntentOf(turn: ConversationTurn): ObservedIntentKey | null {
  const raw = turn.observedIntent as string | undefined;
  if (!raw) return null;
  return (OBSERVED_INTENT_ORDER as readonly string[]).includes(raw) ? (raw as ObservedIntentKey) : 'other';
}

// Geometry of the lane graph. 636 is scale 1: at that width its labels are 11px,
// exactly the page's own small type.
const LANE_X0 = 210;
const LANE_X1 = 630;
const LANE_Y0 = 22.5;
const LANE_ROW_H = 34;
/** text.secondary — the single series hue. Violet means « nous » and nothing else. */
const LANE_MARK = '#6B6B6B';
/** info.dark — the regression. `info`, not `warning`: a regression is a measurement, not a failure. */
const LANE_ACCENT = '#0369A1';
/** warning.dark — D42 contamination. */
const LANE_INDUCED = '#B45309';

interface LanePoint {
  turn: ConversationTurn;
  i: number;
  key: ObservedIntentKey | null;
  depth: number | null;
  failed: boolean;
}

/** The measured funnel trajectory of one run, plus its tabular equivalent. */
function FunnelLanes({ turns }: { turns: ConversationTurn[] }) {
  const { t } = useTranslation();

  const laneLabel = (k: ObservedIntentKey) =>
    k === 'other' ? t('conversations.runV2.laneOther') : t(`conversations.persona.observedIntent_${k}`);

  // The three funnel depths, then one NAMED lane per non-funnel observed intent
  // this run actually produced. Nothing is ever folded into one of the three.
  const extraLanes = OBSERVED_INTENT_ORDER.filter(
    (k) => !isFunnelLane(k) && turns.some((x) => observedIntentOf(x) === k),
  );
  const lanes: ObservedIntentKey[] = [...FUNNEL_LANES, ...extraLanes];
  const yOf = (k: ObservedIntentKey) => LANE_Y0 + LANE_ROW_H * lanes.indexOf(k);
  const step = (LANE_X1 - LANE_X0) / Math.max(turns.length, 1);
  const xOf = (i: number) => LANE_X0 + step * (i + 0.5);

  // A turn with no observed intent is ABSENT from the graph: parking it on a
  // default lane would assert a measurement we never made.
  const pts: LanePoint[] = turns.map((turn, i) => {
    const key = observedIntentOf(turn);
    return {
      turn,
      i,
      key,
      depth: key && isFunnelLane(key) ? FUNNEL_LANES.indexOf(key) : null,
      failed: turn.turnStatus === 'failed',
    };
  });

  // Only CONSECUTIVE turns both sitting on a funnel lane make a segment: nothing
  // is interpolated across a pending, failed or out-of-funnel turn.
  const segments: { a: LanePoint; b: LanePoint; up: boolean }[] = [];
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i];
    const b = pts[i + 1];
    if (a.depth == null || b.depth == null) continue;
    segments.push({ a, b, up: b.depth < a.depth });
  }
  const regressed = new Set(segments.filter((s) => s.up).map((s) => s.b.i));

  const lastY = LANE_Y0 + LANE_ROW_H * (lanes.length - 1);
  const height = lastY + 29.5;

  const labelOf = (p: LanePoint) => {
    if (p.failed) return t('conversations.runTurnFail');
    if (!p.key) return t('conversations.runV2.lanePending');
    if (isFunnelLane(p.key)) return `${laneLabel(p.key)} (${t(DEPTH_SHORT[p.key])})`;
    return laneLabel(p.key);
  };

  const aria = `${t('conversations.runV2.lanesAriaIntro')} ${pts
    .map((p) => {
      const label = labelOf(p);
      if (p.turn.induced) return t('conversations.runV2.lanesAriaInduced', { n: p.turn.turn, label });
      if (regressed.has(p.i)) return t('conversations.runV2.lanesAriaRegression', { n: p.turn.turn, label });
      return t('conversations.runV2.lanesAriaTurn', { n: p.turn.turn, label });
    })
    .join(', ')}.`;

  return (
    <Box sx={{ mt: 1.25 }}>
      {pts.every((p) => p.key === null) ? (
        <Typography variant="body2" color="text.secondary">{t('conversations.runV2.lanesEmpty')}</Typography>
      ) : (
        <Box
          component="svg"
          viewBox={`0 0 636 ${height}`}
          role="img"
          aria-label={aria}
          preserveAspectRatio="xMinYMid meet"
          sx={{ display: 'block', width: 636, maxWidth: '100%', height: 'auto', overflow: 'visible' }}
        >
          <g stroke="#F1F1F4" strokeWidth={1}>
            {pts.map((p) => <line key={p.i} x1={xOf(p.i)} y1={12} x2={xOf(p.i)} y2={lastY + 10} />)}
          </g>
          <g stroke="#E6E7EB" strokeWidth={1}>
            {lanes.map((k) => <line key={k} x1={LANE_X0} y1={yOf(k)} x2={LANE_X1} y2={yOf(k)} />)}
          </g>
          <g fontSize={11} textAnchor="end">
            {lanes.map((k) => (
              <text key={k} x={LANE_X0 - 14} y={yOf(k) + 3.5} fill={isFunnelLane(k) ? LANE_MARK : '#A1A1A1'}>
                {laneLabel(k)}
                {isFunnelLane(k) && <tspan fill="#A1A1A1"> {t(DEPTH_SHORT[k])}</tspan>}
              </text>
            ))}
          </g>
          {/* One series, one hue. The remontée is the only accented segment of the page. */}
          {segments.map((s) => (
            <line
              key={`${s.a.i}-${s.b.i}`}
              x1={xOf(s.a.i)}
              y1={yOf(s.a.key!)}
              x2={xOf(s.b.i)}
              y2={yOf(s.b.key!)}
              stroke={s.up ? LANE_ACCENT : LANE_MARK}
              strokeWidth={s.up ? 2.5 : 2}
            />
          ))}
          <g stroke="#FFFFFF" strokeWidth={2}>
            {pts.map((p) => {
              if (!p.key || p.failed) return null;
              const cx = xOf(p.i);
              const cy = yOf(p.key);
              // A square means exactly one thing: the runtime brand guard fired here (D42).
              if (p.turn.induced) {
                return <rect key={p.i} x={cx - 4.5} y={cy - 4.5} width={9} height={9} rx={2} fill={LANE_INDUCED} />;
              }
              const up = regressed.has(p.i);
              return <circle key={p.i} cx={cx} cy={cy} r={up ? 5.5 : 4.5} fill={up ? LANE_ACCENT : LANE_MARK} />;
            })}
          </g>
          {segments.filter((s) => s.up).map((s) => {
            const nearRight = s.b.i > pts.length / 2;
            return (
              <text
                key={`up-${s.b.i}`}
                x={xOf(s.b.i) + (nearRight ? -13 : 13)}
                y={yOf(s.b.key!) - 9}
                textAnchor={nearRight ? 'end' : 'start'}
                fontSize={10.5}
                fontWeight={700}
                fill={LANE_ACCENT}
              >
                ▴ {t('conversations.runV2.regression')}
              </text>
            );
          })}
          <g fontSize={10} fill="#A1A1A1" textAnchor="middle" fontWeight={700}>
            {pts.map((p) => <text key={p.i} x={xOf(p.i)} y={lastY + 21.5}>{p.turn.turn}</text>)}
          </g>
          <line x1={LANE_X0} y1={lastY + 9} x2={LANE_X1} y2={lastY + 9} stroke="#D1D5DB" strokeWidth={1} />
        </Box>
      )}

      {pts.some((p) => p.turn.induced) && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          <Box component="span" aria-hidden sx={{ color: LANE_INDUCED, fontWeight: 700 }}>▪</Box>
          {' '}{t('conversations.runV2.inducedMark')}
        </Typography>
      )}

      {/* The graph's tabular equivalent, and the one place the two axes sit side by
          side so the difference is readable rather than asserted. */}
      <Details summary={t('conversations.runV2.tableSummary')}>
        <Box sx={{ overflowX: 'auto' }}>
          <Box component="table" sx={{ borderCollapse: 'collapse', minWidth: 440 }}>
            <Box component="thead">
              <Box component="tr">
                <Box component="th" sx={HEAD_SX}>{t('conversations.runV2.colTurn')}</Box>
                <Box component="th" sx={HEAD_SX}>{t('conversations.runV2.colObserved')}</Box>
                <Box component="th" sx={HEAD_SX}>{t('conversations.runV2.colInduced')}</Box>
              </Box>
            </Box>
            <Box component="tbody">
              {pts.map((p) => (
                <Box component="tr" key={p.i}>
                  <Box component="td" sx={{ ...CELL_SX, whiteSpace: 'nowrap' }}>T{p.turn.turn}</Box>
                  <Box component="td" sx={CELL_SX}>
                    {labelOf(p)}
                    {regressed.has(p.i) && (
                      <Box component="span" sx={{ color: LANE_ACCENT, fontWeight: 700, ml: 0.75 }}>
                        ▴ {t('conversations.runV2.regression')}
                      </Box>
                    )}
                  </Box>
                  <Box component="td" sx={CELL_SX}>
                    {p.turn.induced
                      ? <Box component="span" sx={{ color: LANE_INDUCED, fontWeight: 700 }}>⚑</Box>
                      : '·'}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Details>
    </Box>
  );
}

/** One of the five numbers. Dividers, not cards — the Panel already carries the scope. */
function Fig({ label, glyph, value, unit, detail }: {
  label: string;
  glyph?: string;
  value: React.ReactNode;
  unit?: string;
  detail: React.ReactNode;
}) {
  return (
    <Box>
      <Eyebrow>
        {glyph && <Box component="span" aria-hidden sx={{ color: 'success.dark', mr: 0.5 }}>{glyph}</Box>}
        {label}
      </Eyebrow>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '3px', mt: 1 }}>
        <Box component="span" sx={{ fontFamily: '"Inter Tight", "Inter", sans-serif', fontSize: '2.125rem', fontWeight: 700, letterSpacing: '-0.028em', lineHeight: 1.1 }}>
          {value}
        </Box>
        {unit && (
          <Box component="span" sx={{ fontSize: '1.0625rem', fontWeight: 600, color: 'text.secondary' }}>{unit}</Box>
        )}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.875, lineHeight: 1.4 }}>
        {detail}
      </Typography>
    </Box>
  );
}

/**
 * Scope is structural on this page, but it is also said in words once per block:
 * « ce run » is the white 24px-radius Panel, « cette cellule » the flat grey slab.
 */
function ScopeBadge({ kind, label }: { kind: 'run' | 'cell'; label: string }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.5,
        fontSize: '0.6875rem', fontWeight: 700, color: 'text.secondary',
        border: '1px solid', borderColor: 'divider', borderRadius: '6px',
        px: 0.75, py: '2px', whiteSpace: 'nowrap',
        bgcolor: kind === 'cell' ? '#FFFFFF' : 'transparent',
      }}
    >
      <Box component="span" aria-hidden>{kind === 'run' ? '▸' : '▣'}</Box>
      {label}
    </Box>
  );
}

/** Per-brand presence across this run's turns, own brand first. */
interface BrandLine {
  brand: string;
  own: boolean;
  tracked: boolean;
  turnsCited: number;
  lastCitedTurn: number | null;
  firstCitedTurn: number | null;
}

function brandLines(
  turns: ConversationTurn[],
  brandName: string,
  brandAliases: string[],
): BrandLine[] {
  const ownKeys = new Set([brandName, ...brandAliases].map((x) => x.toLowerCase()));
  const tracked = new Set<string>();
  const byBrand = new Map<string, BrandLine>();

  const touch = (brand: string, own: boolean, isTracked: boolean, turn: number) => {
    let line = byBrand.get(brand);
    if (!line) {
      line = { brand, own, tracked: isTracked, turnsCited: 0, lastCitedTurn: null, firstCitedTurn: null };
      byBrand.set(brand, line);
    }
    line.tracked = line.tracked || isTracked;
    line.turnsCited += 1;
    line.lastCitedTurn = turn;
    if (line.firstCitedTurn === null) line.firstCitedTurn = turn;
  };

  for (const turn of turns) for (const c of turn.competitorsMentioned ?? []) tracked.add(c);

  for (const turn of turns) {
    if (turn.cited) touch(brandName, true, true, turn.turn);
    const named = new Set<string>([
      ...(turn.competitorsMentioned ?? []),
      ...(turn.otherBrandsCited ?? []),
      ...Object.keys(turn.lanes ?? {}),
    ]);
    for (const b of named) {
      if (ownKeys.has(b.toLowerCase())) continue; // already counted via `cited`
      touch(b, false, tracked.has(b), turn.turn);
    }
  }

  return [...byBrand.values()].sort((a, b) => {
    if (a.own !== b.own) return a.own ? -1 : 1;
    if (a.tracked !== b.tracked) return a.tracked ? -1 : 1;
    return b.turnsCited - a.turnsCited;
  });
}

/** Cibles matched in this run, by targetId. */
function cibleLines(turns: ConversationTurn[]) {
  const by = new Map<string, { targetId: string; category: string; value: string; turns: number; cit: number; viaSource: boolean }>();
  for (const turn of turns) {
    const seenThisTurn = new Set<string>();
    for (const m of turn.targetMatches ?? []) {
      let row = by.get(m.targetId);
      if (!row) {
        row = { targetId: m.targetId, category: m.category, value: m.value, turns: 0, cit: 0, viaSource: false };
        by.set(m.targetId, row);
      }
      row.cit += 1;
      row.viaSource = row.viaSource || m.via === 'source';
      if (!seenThisTurn.has(m.targetId)) { row.turns += 1; seenThisTurn.add(m.targetId); }
    }
  }
  return [...by.values()].sort((a, b) => b.cit - a.cit);
}

/** Domains this engine cited in this run, grouped by the turn's observed intent. */
function sourceLines(turns: ConversationTurn[]) {
  const by = new Map<string, { domain: string; primaryUrl: string; cit: number; turns: Set<number>; byIntent: Partial<Record<ObservedIntentKey, number>>; byTurn: Map<number, number>; own: boolean }>();
  for (const turn of turns) {
    // Use the Typesafe-classified observed intent; fall back to 'other' when absent.
    const rawIntent = (turn.observedIntent ?? 'other') as string;
    const intent: ObservedIntentKey = (OBSERVED_INTENT_ORDER as readonly string[]).includes(rawIntent)
      ? (rawIntent as ObservedIntentKey)
      : 'other';
    for (let i = 0; i < (turn.sourceLinks ?? []).length; i++) {
      const url = turn.sourceLinks[i];
      // For Gemini/Google: turn.sources[i] carries the readable label (domain or site
      // name); turn.sourceLinks[i] is the vertexaisearch redirect wrapper. Grouping by
      // sourceLinks hostname would collapse every Gemini source into one
      // "vertexaisearch.cloud.google.com" entry. Use sources[i] as the group key when
      // it contains a non-redirect label.
      const rawLabel = (turn.sources ?? [])[i] || url;
      const domain = domainOf(rawLabel) || (rawLabel.startsWith('http') ? null : rawLabel.slice(0, 60)) || domainOf(url);
      if (!domain) continue;
      let row = by.get(domain);
      if (!row) {
        row = { domain, primaryUrl: url, cit: 0, turns: new Set(), byIntent: {}, byTurn: new Map(), own: false };
        by.set(domain, row);
      }
      row.cit += 1;
      row.turns.add(turn.turn);
      row.byIntent[intent] = (row.byIntent[intent] ?? 0) + 1;
      row.byTurn.set(turn.turn, (row.byTurn.get(turn.turn) ?? 0) + 1);
      if ((turn.targetMatches ?? []).some((m) => m.via === 'source' && m.sourceIndex === i && m.kind === 'url')) row.own = true;
    }
  }
  return [...by.values()].sort((a, b) => b.cit - a.cit);
}

const SIGNAL_LABELS: Record<string, string> = {
  cited: 'conversations.runSignalNameCited',
  citedPosition: 'conversations.runSignalNameCitedPosition',
  brandIsSource: 'conversations.runSignalNameBrandIsSource',
  competitorCount: 'conversations.runSignalNameCompetitorCount',
  narrativeAlignment: 'conversations.runSignalNameNarrativeAlignment',
  recommendationStrength: 'conversations.runSignalNameRecommendationStrength',
  sentiment: 'conversations.runSignalNameSentiment',
  eliminated: 'conversations.runSignalNameEliminated',
};

// ---------------------------------------------------------------------------
// SourcesByStage — domain + URL rows split into Exploration / Évaluation / Décision
// Top-10 per stage with expand button; domain rows have favicon.
// ---------------------------------------------------------------------------

const SOURCES_TOP = 10;

type SourceRow = ReturnType<typeof sourceLines>[number];
function SourcesByIntent({
  sources,
  safeTurns,
}: {
  sources: SourceRow[];
  safeTurns: ConversationTurn[];
}) {
  const { t } = useTranslation();
  const [expandedDomain, setExpandedDomain] = useState(false);
  const [expandedUrlDomains, setExpandedUrlDomains] = useState<Set<string>>(new Set());

  const intentOf = (turn: ConversationTurn): ObservedIntentKey => {
    const raw = (turn.observedIntent ?? 'other') as string;
    return (OBSERVED_INTENT_ORDER as readonly string[]).includes(raw) ? (raw as ObservedIntentKey) : 'other';
  };
  const activeIntents = OBSERVED_INTENT_ORDER.filter((k) => safeTurns.some((t) => intentOf(t) === k));

  return (
    <Stack spacing={2.5}>
      {/* Domains by stage */}
      <Box>
        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.6875rem', display: 'block', mb: 1 }}>
          {t('conversations.runSourcesByDomain')}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: `repeat(${Math.min(activeIntents.length, 3)}, minmax(0, 1fr))` }, gap: 2.5 }}>
          {activeIntents.map((intent) => {
            const rows = sources.filter((s) => (s.byIntent[intent] ?? 0) > 0).sort((a, b) => (b.byIntent[intent] ?? 0) - (a.byIntent[intent] ?? 0));
            const visible = expandedDomain ? rows : rows.slice(0, SOURCES_TOP);
            const max = rows[0]?.byIntent[intent] ?? 1;
            return (
              <Box key={intent}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.75 }}>
                  {t(`conversations.persona.observedIntent_${intent}`)}
                </Typography>
                {rows.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    {t('conversations.runStageNoSource')}
                  </Typography>
                ) : (
                  <Stack spacing={0.75}>
                    {visible.map((s) => (
                      <Box key={s.domain}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <DomainFavicon domain={s.domain} name={s.domain} size={16} />
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{ flex: 1, minWidth: 0, fontWeight: s.own ? 700 : 400, color: s.own ? 'primary.main' : undefined, fontSize: '0.8125rem' }}
                            title={s.domain}
                          >
                            {s.domain}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {s.byIntent[intent] ?? 0}
                          </Typography>
                        </Box>
                        <Box sx={{ height: 3, borderRadius: 2, bgcolor: 'action.hover', mt: 0.25 }}>
                          <Box sx={{ height: '100%', borderRadius: 2, bgcolor: s.own ? 'primary.main' : 'text.disabled', width: `${((s.byIntent[intent] ?? 0) / max) * 100}%` }} />
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            );
          })}
        </Box>
        {sources.filter((s) => Object.values(s.byIntent).some((v) => (v ?? 0) > 0)).length > SOURCES_TOP && (
          <Box sx={{ mt: 1.25 }}>
            <Button size="small" variant="text" sx={{ fontSize: '0.75rem', p: 0, minWidth: 0 }} onClick={() => setExpandedDomain((v) => !v)}>
              {expandedDomain ? t('conversations.runSourcesCollapse') : t('conversations.runSourcesExpandDomains', { n: sources.length - SOURCES_TOP })}
            </Button>
          </Box>
        )}
      </Box>

      {/* URLs by intent — grouped by domain */}
      <Box>
        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.6875rem', display: 'block', mb: 1 }}>
          {t('conversations.runSourcesByUrl')}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: `repeat(${Math.min(activeIntents.length, 3)}, minmax(0, 1fr))` }, gap: 2.5 }}>
          {activeIntents.map((intent) => {
            const intentTurnNums = new Set(safeTurns.filter((x) => intentOf(x) === intent).map((x) => x.turn));
            const intentUrlCounts = new Map<string, number>();
            for (const turn of safeTurns) {
              if (!intentTurnNums.has(turn.turn)) continue;
              for (const url of turn.sourceLinks ?? []) {
                if (url) intentUrlCounts.set(url, (intentUrlCounts.get(url) ?? 0) + 1);
              }
            }
            const allRows = [...intentUrlCounts.entries()].map(([url, cit]) => ({ url, cit })).sort((a, b) => b.cit - a.cit);

            // Group by domain
            const domainGroupMap = new Map<string, { urls: { url: string; cit: number }[]; total: number }>();
            for (const { url, cit } of allRows) {
              const domain = domainOf(url) ?? url;
              if (!domainGroupMap.has(domain)) domainGroupMap.set(domain, { urls: [], total: 0 });
              const g = domainGroupMap.get(domain)!;
              g.urls.push({ url, cit });
              g.total += cit;
            }
            const domainGroups = [...domainGroupMap.entries()]
              .map(([domain, g]) => ({ domain, ...g }))
              .sort((a, b) => b.total - a.total);

            return (
              <Box key={intent}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.75 }}>
                  {t(`conversations.persona.observedIntent_${intent}`)}
                </Typography>
                {domainGroups.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    {t('conversations.runStageNoSource')}
                  </Typography>
                ) : (
                  <Stack spacing={0.5}>
                    {domainGroups.map(({ domain, urls: domainUrls, total }) => {
                      const expandKey = `${intent}:${domain}`;
                      const isOpen = expandedUrlDomains.has(expandKey);
                      return (
                        <Box key={domain}>
                          {/* Domain header row */}
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: domainUrls.length > 1 ? 'pointer' : 'default', userSelect: 'none' }}
                            onClick={() => {
                              if (domainUrls.length <= 1) return;
                              setExpandedUrlDomains((prev) => {
                                const next = new Set(prev);
                                if (next.has(expandKey)) next.delete(expandKey); else next.add(expandKey);
                                return next;
                              });
                            }}
                          >
                            <DomainFavicon domain={domain} name={domain} size={14} />
                            <Typography variant="body2" noWrap title={domain} sx={{ flex: 1, minWidth: 0, fontSize: '0.8125rem' }}>
                              {domain}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                              {total}
                            </Typography>
                            {domainUrls.length > 1 && (
                              <Typography component="span" sx={{ fontSize: '0.6875rem', color: 'text.disabled', flexShrink: 0 }}>
                                {isOpen ? '▴' : '▾'}
                              </Typography>
                            )}
                          </Box>
                          {/* Expanded URLs */}
                          {isOpen && domainUrls.map(({ url, cit }) => (
                            <Box key={url} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pl: 2.5, mt: 0.375 }}>
                              <Typography
                                component="a"
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="body2"
                                noWrap
                                title={url}
                                sx={{ flex: 1, minWidth: 0, fontSize: '0.75rem', color: 'text.secondary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                              >
                                {truncateUrl(url, 38)}
                              </Typography>
                              <Typography variant="caption" color="text.disabled" sx={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                                {cit}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function ConversationRunPage() {
  const { t, i18n } = useTranslation();
  const { personaId, launchId, cellPath, idx: idxStr } = useParams<{
    personaId: string; launchId: string; cellPath: string; idx: string;
  }>();
  const { activeBrand: brand } = useBrand();
  const navigate = useNavigate();
  const runIdx = Number(idxStr);

  const [persona, setPersona] = useState<ConversationPersona | null>(null);
  const [cells, setCells] = useState<(ConversationSubRun | null)[]>([]);
  const [extras, setExtras] = useState<ConversationSubRunExtras | null>(null);
  const [turns, setTurns] = useState<ConversationTurn[] | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'notfound' | 'error'>('loading');
  const [turnsState, setTurnsState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [addedBrands, setAddedBrands] = useState<string[]>([]);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [followDialogLabel, setFollowDialogLabel] = useState<string | null>(null);
  const [resolvedDomains, setResolvedDomains] = useState<Map<string, { name: string; domain: string }>>(new Map());
  const [runLens, setRunLens] = useState<'brands' | 'cibles' | 'sources'>('brands');
  const [brandsExpanded, setBrandsExpanded] = useState(false);

  const brandId = brand?.brandId;
  const parsed = cellPath ? parseCellPath(cellPath) : null;

  // ---- matrix: the persona, every cell, and every cell's runs[] ----
  useEffect(() => {
    if (!brandId || !personaId || !launchId) return;
    let cancelled = false;
    setState('loading');
    api.get<{ persona: ConversationPersona; cells: (ConversationSubRun | null)[] }>(
      `/brands/${brandId}/personas/${personaId}/launches/${launchId}/matrix`,
    ).then((r) => {
      if (cancelled) return;
      setPersona(r.persona);
      setCells(r.cells ?? []);
      setState('ready');
    }).catch((err) => {
      if (cancelled) return;
      setState(err instanceof ApiError && err.status === 404 ? 'notfound' : 'error');
    });
    return () => { cancelled = true; };
  }, [brandId, personaId, launchId]);

  // ---- this cell's extras (context: the cell's domains across all its runs) ----
  useEffect(() => {
    if (!brandId || !personaId || !launchId || !cellPath) return;
    let cancelled = false;
    setExtras(null);
    api.get<ConversationSubRunExtras>(
      `/brands/${brandId}/personas/${personaId}/launches/${launchId}/cells/${cellPath}/sources`,
    ).then((r) => { if (!cancelled) setExtras(r); }).catch(() => { /* the page works without it */ });
    return () => { cancelled = true; };
  }, [brandId, personaId, launchId, cellPath]);

  // ---- this run's turns ----
  useEffect(() => {
    if (!brandId || !personaId || !launchId || !cellPath || !Number.isInteger(runIdx)) return;
    let cancelled = false;
    setTurnsState('loading');
    setTurns(null);
    api.get<{ turns: ConversationTurn[] }>(
      `/brands/${brandId}/personas/${personaId}/launches/${launchId}/cells/${cellPath}/conversations/${runIdx}`,
    ).then((r) => {
      if (cancelled) return;
      setTurns([...(r.turns ?? [])].sort((a, b) => a.turn - b.turn));
      setTurnsState('ready');
    }).catch(() => { if (!cancelled) setTurnsState('error'); });
    return () => { cancelled = true; };
  }, [brandId, personaId, launchId, cellPath, runIdx]);

  // ---- the cell this run belongs to ----
  const cellIdx = useMemo(() => {
    if (!persona || !parsed) return -1;
    return persona.cells.findIndex(
      (c) => c.engine === parsed.engine && c.language === parsed.language && c.zone === parsed.zone,
    );
  }, [persona, parsed]);

  const cell = cellIdx >= 0 ? (cells[cellIdx] ?? null) : null;
  const runs = useMemo(
    () => (Array.isArray(cell?.runs) ? [...cell.runs].sort((a, b) => a.idx - b.idx) : []),
    [cell],
  );
  const run = runs.find((r) => r.idx === runIdx) ?? null;

  const goTo = useCallback((nextCellPath: string, nextIdx: number) => {
    navigate(`/brand/conversations/${personaId}/launches/${launchId}/cells/${nextCellPath}/runs/${nextIdx}`);
  }, [navigate, personaId, launchId]);

  /** The CSV endpoint is guarded, so it needs the Bearer token — not a bare href. */
  const [exporting, setExporting] = useState(false);
  const handleExport = useCallback(async () => {
    if (!brandId) return;
    setExporting(true);
    try {
      const blob = await api.getBlob(
        `/brands/${brandId}/personas/${personaId}/launches/${launchId}/cells/${cellPath}/export.csv`,
      );
      // Above 10 000 rows the endpoint answers 202 + { jobId } instead of the file (§4.7).
      if (blob.type.includes('json')) {
        setToast({ message: t('conversations.runExportQueued'), severity: 'success' });
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `conversation-${launchId}-${cellPath}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setToast({
        message: err instanceof ApiError ? err.message : t('conversations.editorErrorGeneric'),
        severity: 'error',
      });
    } finally {
      setExporting(false);
    }
  }, [brandId, personaId, launchId, cellPath, t]);

  // Opens the CompetitorFollowDialog for domain confirmation — the actual save
  // happens inside the dialog after the user reviews/edits the resolved domain.
  const handleAddCompetitor = useCallback((label: string) => {
    setFollowDialogLabel(label);
  }, []);

  // ---- derived from turns ----
  const safeTurns = turns ?? [];
  const dropOut = useMemo(() => dropOutTurnOf(safeTurns), [safeTurns]);
  const lines = useMemo(
    () => brandLines(safeTurns, brand?.name ?? '', (brand?.strategy?.aliases ?? []) as string[]),
    [safeTurns, brand?.name, brand?.strategy?.aliases],
  );
  const cibles = useMemo(() => cibleLines(safeTurns), [safeTurns]);
  const sources = useMemo(() => sourceLines(safeTurns), [safeTurns]);
  const untracked = useMemo(() => lines.filter((l) => !l.own && !l.tracked).map((l) => l.brand), [lines]);

  /**
   * Candidate domains for competitor resolution: every unique hostname this run
   * cited, minus the ones that matched our OWN brand (a competitor can never
   * resolve to the client's own site). Computed once and shared by the name →
   * domain resolution below and by CompetitorFollowDialog.
   */
  const sourceDomains = useMemo(() => {
    const own = new Set(sources.filter((s) => s.own).map((s) => s.domain));
    const seen = new Set<string>();
    for (const turn of safeTurns) {
      for (const link of turn.sourceLinks ?? []) {
        try {
          const host = new URL(link).hostname.replace(/^www\./, '');
          if (host && !own.has(host)) seen.add(host);
        } catch { /* skip */ }
      }
    }
    return [...seen];
  }, [safeTurns, sources]);

  // ---- resolve each untracked competitor NAME to one of this run's domains ----
  // This is the shape resolveDomainFromSources was designed for: a REAL brand name
  // plus the FULL candidate list, over which it builds a TypeSafe "choice" question
  // with an explicit `unknown` escape. No `source` gate: with a real name the free
  // token-match is legitimate, and the no-answer cases (unknown, confidence < 0.4)
  // already come back as { domain: null } server-side — which we render as nothing.
  useEffect(() => {
    if (!brandId || untracked.length === 0 || sourceDomains.length === 0) return;
    let cancelled = false;
    Promise.allSettled(
      untracked.map(async (name) => {
        try {
          const data = await api.post<{ domain: string | null; confidence: number; source: string }>(
            `/brands/${brandId}/competitors/resolve-domain`,
            { label: name, sourceDomains },
          );
          if (!data?.domain) return null;
          return { name, domain: data.domain };
        } catch {
          return null;
        }
      }),
    ).then((results) => {
      if (cancelled) return;
      const map = new Map<string, { name: string; domain: string }>();
      for (const r of results) {
        // Keyed on the lowercased competitor name — consumers hold the name, not the domain.
        if (r.status === 'fulfilled' && r.value) map.set(r.value.name.toLowerCase(), r.value);
      }
      setResolvedDomains(map);
    });
    return () => { cancelled = true; };
  }, [brandId, untracked, sourceDomains]);

  // ---- loading / error shells ----
  if (state === 'loading') {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rectangular" height={40} sx={{ mb: 2, borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={260} sx={{ mb: 2, borderRadius: 2 }} />
        <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }
  if (state !== 'ready' || !persona || !parsed) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{t('conversations.runNotFound')}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/brand/conversations/${personaId}`)}>
          {t('conversations.runBackToPersona')}
        </Button>
      </Box>
    );
  }

  const intentId = cell?.config?.intentId ?? persona.intentId;
  const intent = CONVERSATION_INTENTS[intentId];
  const engineLabel = ENGINE_META[engineKey(parsed.engine)]?.label ?? parsed.engine;
  const zoneLabel = persona.cells[cellIdx]?.zoneMeta?.label ?? parsed.zone;
  const failed = run ? squareKind(run) === 'fail' : false;

  // Zone/engine options for the picker
  const zoneKeys: string[] = [];
  for (const c of persona.cells) {
    const k = `${c.zone}|${c.language}`;
    if (!zoneKeys.includes(k)) zoneKeys.push(k);
  }
  const currentZoneKey = `${parsed.zone}|${parsed.language}`;
  const enginesForZone = persona.cells
    .filter((c) => `${c.zone}|${c.language}` === currentZoneKey)
    .map((c) => c.engine);

  // Comparison line: this run vs this configuration vs the whole launch (§4.5.4)
  const cellPresence = cell?.presence ?? null;
  const launchPresence = (() => {
    let present = 0;
    let total = 0;
    for (const c of cells) {
      if (!c?.stats) continue;
      present += c.stats.sumPresentTurns;
      total += c.stats.sumTurns;
    }
    return total > 0 ? present / total : null;
  })();

  // ---- the presence denominator, D42, same rule as the finalizer ----
  // Turns strictly before the first induced turn, completed only. Every number in
  // block 2 uses this set so none of them can disagree with `run.presence`.
  const firstInducedTurn = safeTurns.find((x) => x.induced)?.turn ?? null;
  const retained = safeTurns.filter(
    (x) => (firstInducedTurn === null || x.turn < firstInducedTurn) && x.turnStatus === 'completed',
  );
  const retainedCited = retained.filter((x) => x.cited).length;

  // ---- the lede: the brand's own trajectory, in one sentence ----
  const citedTurnList = safeTurns.filter((x) => x.cited).map((x) => x.turn);
  // A brand first seen AT the drop-out turn is the one that took the place.
  const entrantsAtDrop = dropOut === null
    ? []
    : lines.filter((l) => !l.own && l.firstCitedTurn === dropOut);

  // ---- « Présence par intention observée », run-scoped ----
  // Computed from THIS run's retained turns. `presentTurnsByObservedIntent` /
  // `turnsByObservedIntent` aggregate every run of the cell, so they appear only
  // as the explicitly-labelled « cette cellule » rate beside each row.
  const rollup = OBSERVED_INTENT_ORDER
    .map((k) => {
      const rows = retained.filter((x) => observedIntentOf(x) === k);
      const cellTurns = cell?.stats?.turnsByObservedIntent?.[k];
      const cellPresent = cell?.stats?.presentTurnsByObservedIntent?.[k];
      return {
        key: k,
        turns: rows.length,
        cited: rows.filter((x) => x.cited).length,
        cellRate: cellTurns != null && cellTurns > 0 && cellPresent != null ? cellPresent / cellTurns : null,
      };
    })
    .filter((r) => r.turns > 0);
  const rollupSummed = rollup.reduce((a, r) => a + r.turns, 0);
  const pendingTurns = retained.filter((x) => observedIntentOf(x) === null).map((x) => x.turn);

  // ---- the configured intent, measured against what was observed ----
  const attachedTurns = retained.filter((x) => {
    const k = observedIntentOf(x);
    return k !== null && isFunnelLane(k);
  });
  const sameDepthTurns = attachedTurns.filter((x) => observedIntentOf(x) === intentId).length;

  // ---- Rang: first and last readable rank. `citedPosition` is null when the
  // answer named the brand without ranking it, which is not a last place.
  const rankedTurns = safeTurns.filter((x) => x.cited && x.citedPosition != null);
  const firstRanked = rankedTurns[0] ?? null;
  const lastRanked = rankedTurns.length > 0 ? rankedTurns[rankedTurns.length - 1] : null;

  // ---- Alignement: null means the answer never discussed the brand, not 0 ----
  const alignedTurns = safeTurns.filter((x) => x.narrativeAlignment != null).length;

  // ---- Part de nos domaines: owned citations over this run's citations ----
  const ownCitations = sources.reduce((a, s) => a + (s.own ? s.cit : 0), 0);
  const allCitations = sources.reduce((a, s) => a + s.cit, 0);

  /** The outcome ladder; `reached` is the step this run landed on. */
  const scale: { key: 'win' | 'part' | 'lose'; label: string }[] = [
    { key: 'win', label: intent.outcomes.win },
    { key: 'part', label: intent.outcomes.part },
    { key: 'lose', label: intent.outcomes.lose },
  ];
  // Same partition as the platform's win/part/lose triple (`countersToSlice`):
  // win = cReco ('R'), part = cKept - cReco ('M'), lose = n - cKept (never cited OR
  // dropped out before the end). A digit outcome is a LOSE, not a partial.
  const reached: 'win' | 'part' | 'lose' | null = !run || failed
    ? null
    : run.outcome === 'R' ? 'win' : run.outcome === 'M' ? 'part' : 'lose';

  const launchDate = (() => {
    try {
      return new Date(launchTs(launchId!)).toLocaleString(i18n.language, {
        day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return launchId ?? '';
    }
  })();

  // D29 — the four reasons are four different verdicts and are never merged:
  // 'stopped_early' (added 2026-09-25) is a run the persona abandoned, the opposite
  // diagnosis of 'max_turns', so it must not fall through to « tours maximum atteint ».
  const endedByLabel = run
    ? run.endedBy === 'agent_says_done' ? t('conversations.runEndedAgent')
      : run.endedBy === 'script_done' ? t('conversations.runEndedScript')
        : run.endedBy === 'stopped_early' ? t('conversations.runEndedStoppedEarly')
          : t('conversations.runEndedMaxTurns')
    : null;

  return (
    <Box sx={{ p: 3, maxWidth: 1360, mx: 'auto', bgcolor: '#f8f7fc', minHeight: '100vh', ml: -3, mr: -3, px: 3 }}>
      {/* ---------- Breadcrumb ---------- */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <IconButton size="small" onClick={() => navigate(`/brand/conversations/${personaId}`)}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          onClick={() => navigate('/brand/conversations')}
        >
          Conversations
        </Typography>
        <Typography variant="body2" color="text.secondary">›</Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          onClick={() => navigate(`/brand/conversations/${personaId}`)}
        >
          {persona.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">›</Typography>
        <Typography variant="body2" fontWeight={600}>
          {t('conversations.runCrumb', { n: runIdx + 1 })}
        </Typography>
      </Box>

      {/* ---------- 1. Run picker ---------- */}
      <Panel sx={{ mb: 2, py: 1.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Eyebrow>{t('conversations.runPickerZone')}</Eyebrow>
            <Select
              size="small"
              value={currentZoneKey}
              onChange={(e) => {
                const [zone, language] = String(e.target.value).split('|');
                // Keep the engine when the new zone measures it; otherwise take its first.
                const candidates = persona.cells.filter((c) => c.zone === zone && c.language === language);
                const next = candidates.find((c) => c.engine === parsed.engine) ?? candidates[0];
                if (next) goTo(buildCellPath(next.engine as ConversationEngine, next.language, next.zone), 0);
              }}
              sx={{ mt: 0.5, minWidth: 200 }}
            >
              {zoneKeys.map((k) => {
                const [zone, language] = k.split('|');
                const cell = persona.cells.find((c) => c.zone === zone && c.language === language);
                const label = cell?.zoneMeta?.label ?? zone;
                const cc = cell?.zoneMeta?.countryCode;
                const flag = cc ? [...cc.toUpperCase()].map((c) => String.fromCodePoint(0x1f1e6 - 65 + c.charCodeAt(0))).join('') : '';
                return (
                  <MenuItem key={k} value={k}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      {flag && <span style={{ fontSize: '1.1em', lineHeight: 1 }}>{flag}</span>}
                      <span>{label}</span>
                      <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: '3px', px: '4px', py: 0, lineHeight: 1.6 }}>{language}</Box>
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </Box>

          <Box>
            <Eyebrow>{t('conversations.runPickerEngine')}</Eyebrow>
            <Select
              size="small"
              value={parsed.engine}
              onChange={(e) => goTo(buildCellPath(String(e.target.value) as ConversationEngine, parsed.language, parsed.zone), 0)}
              sx={{ mt: 0.5, minWidth: 180 }}
            >
              {enginesForZone.map((eng) => (
                <MenuItem key={eng} value={eng}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EngineLogo engine={eng} size={16} />
                    {ENGINE_META[engineKey(eng)]?.label ?? eng}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box>
            <Eyebrow>{t('conversations.runPickerRun')}</Eyebrow>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <IconButton
                size="small"
                aria-label={t('conversations.runPrev')}
                disabled={runs.findIndex((r) => r.idx === runIdx) <= 0}
                onClick={() => {
                  const i = runs.findIndex((r) => r.idx === runIdx);
                  if (i > 0) goTo(cellPath!, runs[i - 1].idx);
                }}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <Select<number | ''>
                size="small"
                value={runs.some((r) => r.idx === runIdx) ? runIdx : ''}
                onChange={(e) => goTo(cellPath!, Number(e.target.value))}
                sx={{ minWidth: 140 }}
                displayEmpty
                renderValue={(v) => (v === '' ? t('conversations.runsNone') : t('conversations.runPickerRunN', { n: Number(v) + 1, total: runs.length }))}
              >
                {runs.map((r) => (
                  <MenuItem key={r.idx} value={r.idx}>
                    {t('conversations.transcriptRun', { n: r.idx + 1 })} · {squareDesc(r, t)}
                  </MenuItem>
                ))}
              </Select>
              <IconButton
                size="small"
                aria-label={t('conversations.runNext')}
                disabled={(() => { const i = runs.findIndex((r) => r.idx === runIdx); return i < 0 || i >= runs.length - 1; })()}
                onClick={() => {
                  const i = runs.findIndex((r) => r.idx === runIdx);
                  if (i >= 0 && i < runs.length - 1) goTo(cellPath!, runs[i + 1].idx);
                }}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            variant="outlined"
            startIcon={exporting ? <CircularProgress size={14} /> : <DownloadIcon />}
            disabled={exporting}
            onClick={handleExport}
          >
            {t('conversations.runExportCsv')}
          </Button>
        </Box>
      </Panel>

      {!run ? (
        <Alert severity="info">{t('conversations.runNotFinalized')}</Alert>
      ) : (
        <>
          {/* ---------- 2. Ce run ---------- */}
          {/* One white 24px-radius Panel with the product's panel shadow = « ce run ».
              Everything stated inside it is derived from this run's own turns; the two
              cell-scoped elements name themselves (the « cette cellule » rates in the
              roll-up, and the flat grey other-runs slab at the bottom). */}
          <Panel sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
              <Eyebrow>
                {t('conversations.runV2.headEyebrow', { intent: intent.name, n: runIdx + 1, total: runs.length })}
              </Eyebrow>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <EngineLogo engine={parsed.engine} size={16} />
                <Typography variant="body2" fontWeight={600}>
                  {engineLabel} · {parsed.language} · {zoneLabel}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }} />
              <ScopeBadge kind="run" label={t('conversations.runV2.badgeRun')} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {t('conversations.runMetaLaunch')} {launchDate} · {formatDuration(run.durationMs)} · {t('conversations.runCredits', { count: run.credits })} · {endedByLabel}
            </Typography>

            {failed ? (
              <Box sx={{ mt: 2, p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
                <Typography fontWeight={700}>{t('conversations.runFailedTitle')}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t('conversations.runFailedBody')}
                </Typography>
              </Box>
            ) : (
              <>
                {/* The verdict in one sentence. Violet = « nous » and nothing else on
                    this page; the rupture clause is error.dark and keeps the word. */}
                {turnsState === 'ready' ? (
                  <Typography
                    sx={{
                      mt: 2, maxWidth: '44em',
                      fontFamily: '"Inter Tight", "Inter", sans-serif',
                      fontSize: '1.5rem', fontWeight: 600, letterSpacing: '-0.018em', lineHeight: 1.3,
                    }}
                  >
                    <Box component="span" sx={{ color: 'primary.dark', fontWeight: 700 }}>{brand?.name ?? ''}</Box>
                    {' '}
                    {citedTurnList.length === 0
                      ? t('conversations.runV2.ledeNever')
                      : t('conversations.runV2.ledeCitedTurns', { count: citedTurnList.length, turns: citedTurnList.join(', ') })}
                    {dropOut !== null && (
                      <>
                        {' '}
                        <Box component="span" sx={{ color: 'error.dark', fontWeight: 700 }}>
                          {entrantsAtDrop.length === 0
                            ? t('conversations.runV2.ledeDrop', { turn: dropOut })
                            : entrantsAtDrop.length === 1
                              ? t('conversations.runV2.ledeDropWho', { turn: dropOut, who: entrantsAtDrop[0].brand })
                              : t('conversations.runV2.ledeDropWhoMany', { turn: dropOut, who: entrantsAtDrop[0].brand, count: entrantsAtDrop.length - 1 })}
                        </Box>
                      </>
                    )}
                  </Typography>
                ) : (
                  <Skeleton variant="rectangular" height={42} sx={{ mt: 2, borderRadius: 1, maxWidth: 560 }} />
                )}
                {firstInducedTurn !== null && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25, maxWidth: '36em' }}>
                    {t('conversations.runV2.ledeNoteInduced', { turn: firstInducedTurn, brand: brand?.name ?? '' })}
                  </Typography>
                )}

                {/* The outcome ladder, in the configured intent's OWN vocabulary.
                    Colour sits on the glyph, never on the label. */}
                <Box
                  role="group"
                  aria-label={t('conversations.runV2.ladderAria')}
                  sx={{ display: 'flex', alignItems: 'center', mt: 3, flexWrap: 'wrap', fontSize: '0.78125rem' }}
                >
                  {scale.map((s, i) => {
                    const on = reached === s.key;
                    const glyph = s.key === 'win' ? '✓' : s.key === 'part' ? '●' : '▾';
                    const tone = s.key === 'win' ? 'success.dark' : s.key === 'part' ? 'warning.dark' : 'error.dark';
                    return (
                      <Box
                        key={s.key}
                        aria-current={on ? 'true' : undefined}
                        sx={{
                          px: 1.5, pl: i === 0 ? 0 : 1.5,
                          borderLeft: i === 0 ? 0 : '1px solid', borderColor: 'divider',
                          color: on ? 'text.primary' : 'text.secondary', fontWeight: on ? 600 : 400,
                        }}
                      >
                        {on && <Box component="span" aria-hidden sx={{ color: tone, fontWeight: 700, mr: 0.625 }}>{glyph}</Box>}
                        {s.label}
                      </Box>
                    );
                  })}
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 1.75, pl: 1.75, borderLeft: '1px solid', borderColor: 'divider' }}
                  >
                    {t('conversations.runV2.ladderScale', { intent: intent.name, depth: t(DEPTH_LABEL[intentId]) })}
                  </Typography>
                </Box>

                {/* The funnel strip. Column 1 states the configured intent once;
                    column 2 is the MEASURED trajectory, top of funnel at the top so a
                    regression reads as a line going up; column 3 rolls that up with the
                    presence denominator. `stage` appears nowhere in here. */}
                <Box
                  sx={{
                    mt: 3, borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'divider',
                    display: 'grid', alignItems: 'start',
                    gridTemplateColumns: { lg: 'minmax(0, 300px) minmax(0, 1fr) minmax(0, 276px)' },
                    '& > *': { px: { lg: 3.25 }, py: 2.25, borderLeft: { lg: '1px solid' }, borderColor: 'divider' },
                    '& > *:first-of-type': { borderLeft: 0, pl: 0 },
                    '& > *:last-of-type': { pr: 0 },
                  }}
                >
                  <Box>
                    <Eyebrow>{t('conversations.runV2.configuredTitle')}</Eyebrow>
                    <Typography sx={{ mt: 1.125, fontSize: '0.875rem', lineHeight: 1.4 }}>
                      <Box component="span" sx={{ fontWeight: 700 }}>{intent.name}</Box>
                      {' '}
                      <Box component="span" sx={{ color: 'primary.dark', fontWeight: 600 }}>{t(DEPTH_LABEL[intentId])}</Box>
                      <br />
                      <Box component="span" sx={{ fontFamily: 'Georgia, "Times New Roman", serif', color: 'text.secondary' }}>
                        « {intent.exampleQuestion} »
                      </Box>
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, lineHeight: 1.45 }}>
                      {t('conversations.runV2.configuredProse', {
                        meaning: t(DEPTH_MEANING[intentId]),
                        win: intent.outcomes.win,
                        lose: intent.outcomes.lose,
                      })}
                      {attachedTurns.length > 0 && ` ${t('conversations.runV2.configuredMatch', { same: sameDepthTurns, total: attachedTurns.length })}`}
                    </Typography>
                  </Box>

                  <Box>
                    <Eyebrow>{t('conversations.runV2.observedTitle')}</Eyebrow>
                    {turnsState === 'ready' && safeTurns.length > 0 ? (
                      <FunnelLanes turns={safeTurns} />
                    ) : (
                      <Skeleton variant="rectangular" height={124} sx={{ mt: 1.25, borderRadius: 1 }} />
                    )}
                  </Box>

                  <Box>
                    <Eyebrow>{t('conversations.runV2.rollupTitle')}</Eyebrow>
                    {rollup.length === 0 ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>
                        {t('conversations.runV2.lanesEmpty')}
                      </Typography>
                    ) : (
                      <>
                        <Box component="dl" sx={{ m: 0, mt: 1.25, fontSize: '0.71875rem' }}>
                          {rollup.map((r, i) => (
                            <Box
                              key={r.key}
                              sx={{
                                display: 'flex', alignItems: 'baseline', gap: 1, pb: 0.5,
                                pt: i === 0 ? 0 : 0.5,
                                borderTop: i === 0 ? 0 : '1px solid', borderColor: 'divider',
                              }}
                            >
                              <Box component="dt" sx={{ flex: '1 1 auto', minWidth: 0, color: 'text.secondary' }}>
                                {r.key === 'other'
                                  ? t('conversations.runV2.laneOther')
                                  : t(`conversations.persona.observedIntent_${r.key}`)}
                                {isFunnelLane(r.key) && (
                                  <Box component="span" sx={{ color: 'text.disabled', fontSize: '0.65625rem', ml: 0.5 }}>
                                    {t(DEPTH_SHORT[r.key])}
                                  </Box>
                                )}
                                {r.cellRate != null && (
                                  <Box component="span" sx={{ display: 'block', color: 'text.disabled', fontSize: '0.65625rem' }}>
                                    <Box component="span" aria-hidden>▣</Box>{' '}
                                    {t('conversations.runV2.rollupCell', { rate: pct(r.cellRate) })}
                                  </Box>
                                )}
                              </Box>
                              <Box component="dd" sx={{ m: 0, flex: 'none', fontWeight: 600 }}>
                                {r.cited}
                                <Box component="span" sx={{ color: 'text.disabled', fontWeight: 400, ml: 0.375 }}>
                                  {t('conversations.runV2.rollupTotal', { count: r.turns })}
                                </Box>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                        <Typography
                          variant="caption"
                          color="text.disabled"
                          sx={{ display: 'block', mt: 1.125, fontSize: '0.6875rem', lineHeight: 1.45 }}
                        >
                          {t('conversations.runV2.rollupBase', { summed: rollupSummed, total: retained.length })}
                          {pendingTurns.length > 0 && ` ${t('conversations.runV2.rollupPending', { count: pendingTurns.length, turns: pendingTurns.join(', ') })}`}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Box>

                {/* The five numbers. A figure with no value is ABSENT, never a 0, and
                    each one names the scope its detail line is about. */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(5, minmax(0, 1fr))' },
                    borderBottom: '1px solid', borderColor: 'divider',
                    '& > *': { px: 2.75, py: 2.5, borderLeft: '1px solid', borderColor: 'divider' },
                    '& > *:first-of-type': { borderLeft: 0, pl: 0 },
                  }}
                >
                  <Fig
                    label={t('conversations.runPresence')}
                    value={pct(run.presence) ?? '—'}
                    detail={(
                      <>
                        {turnsState === 'ready' && `${t('conversations.runV2.figPresenceDetail', { cited: retainedCited, total: retained.length })}, `}
                        <b>{t('conversations.runV2.scopeRun')}</b>.
                        {(cellPresence != null || launchPresence != null) && (
                          <>
                            <br />
                            {t('conversations.runV2.figPresenceCompare', { cell: pct(cellPresence) ?? '—', launch: pct(launchPresence) ?? '—' })}
                          </>
                        )}
                      </>
                    )}
                  />

                  {firstRanked && lastRanked && (
                    <Fig
                      label={t('conversations.runV2.figRank')}
                      value={firstRanked.turn === lastRanked.turn ? (
                        firstRanked.citedPosition
                      ) : (
                        <>
                          {firstRanked.citedPosition}
                          <Box component="span" sx={{ fontSize: '1.1875rem', fontWeight: 400, color: 'text.disabled', mx: '2px' }}>→</Box>
                          {lastRanked.citedPosition}
                        </>
                      )}
                      detail={(
                        <>
                          {firstRanked.turn === lastRanked.turn
                            ? t('conversations.runV2.figRankSingle', { turn: firstRanked.turn })
                            : t('conversations.runV2.figRankDetail', { from: firstRanked.turn, to: lastRanked.turn })}
                          {', '}
                          <b>{t('conversations.runV2.scopeRun')}</b>.
                          {lastRanked.turn < (safeTurns[safeTurns.length - 1]?.turn ?? 0) && (
                            <>
                              <br />
                              {t('conversations.runV2.figRankNoneAfter')}
                            </>
                          )}
                        </>
                      )}
                    />
                  )}

                  {run.meanAlignment != null && turnsState === 'ready' && (
                    <Fig
                      label={t('conversations.runV2.figAlignment')}
                      value={Math.round(run.meanAlignment)}
                      unit="/100"
                      detail={(
                        <>
                          {t('conversations.runV2.figAlignmentDetail', { count: alignedTurns })}
                          {', '}
                          <b>{t('conversations.runV2.scopeRun')}</b>.
                        </>
                      )}
                    />
                  )}

                  {run.sourceTurns > 0 && (
                    <Fig
                      label={t('conversations.runV2.figSource')}
                      glyph="✓"
                      value={run.sourceTurns}
                      unit={t('conversations.runV2.figSourceUnit', { count: run.sourceTurns })}
                      detail={(
                        <>
                          {t('conversations.runV2.figSourceDetail', { total: run.turnsCompleted })}
                          {', '}
                          <b>{t('conversations.runV2.scopeRun')}</b>.
                        </>
                      )}
                    />
                  )}

                  {allCitations > 0 && (
                    <Fig
                      label={t('conversations.runV2.figOwnShare')}
                      value={pct(ownCitations / allCitations) ?? '—'}
                      detail={(
                        <>
                          {t('conversations.runV2.figOwnShareDetail', { own: ownCitations, total: allCitations })}
                          {', '}
                          <b>{t('conversations.runV2.scopeRun')}</b>.
                        </>
                      )}
                    />
                  )}
                </Box>
              </>
            )}

            {/* The ONE cell-scoped element of this block, dressed as such:
                flat #F3F4F6, 12px radius, grey left bar, no shadow. */}
            {runs.length > 1 && (
              <Box
                sx={{
                  mt: 2.5, px: 2, py: 1.75,
                  bgcolor: '#F3F4F6', borderRadius: '12px',
                  border: '1px solid #D1D5DB', borderLeft: '3px solid #D1D5DB',
                  boxShadow: 'none',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Eyebrow>{t('conversations.runOtherRuns')}</Eyebrow>
                  <ScopeBadge kind="cell" label={t('conversations.runV2.badgeCell')} />
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                  {runs.map((r) => (
                    <RunSquare
                      key={r.idx}
                      run={r}
                      selected={r.idx === runIdx}
                      onClick={() => goTo(cellPath!, r.idx)}
                      label={`${t('conversations.transcriptRun', { n: r.idx + 1 })} — ${squareDesc(r, t)}`}
                    />
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  {t('conversations.runV2.otherRunsHint')}
                </Typography>
              </Box>
            )}
          </Panel>

          {turnsState === 'error' && (
            <Alert severity="warning" sx={{ mb: 2 }}>{t('conversations.transcriptError')}</Alert>
          )}

          {/* ---------- 4. Brands · Cibles · Sources ---------- */}
          {turnsState === 'ready' && (
            <Panel sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                  {t('conversations.runDataSectionTitle')}
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={runLens}
                  onChange={(_e, v) => { if (v) setRunLens(v); }}
                  aria-label={t('conversations.runDataTabsAria')}
                  sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.5, fontSize: '0.75rem', textTransform: 'none', fontWeight: 600 } }}
                >
                  <ToggleButton value="brands">{t('conversations.runTabBrands')}</ToggleButton>
                  <ToggleButton value="cibles">{t('conversations.runTabCibles')}</ToggleButton>
                  <ToggleButton value="sources">{t('conversations.runTabSources')}</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* Marques */}
              <Box sx={{ display: runLens === 'brands' ? undefined : 'none' }}>
                <SectionTitle>{t('conversations.runBrandsTitle')}</SectionTitle>
                {/* If only own brand + analysis still pending → show a hint */}
                {lines.length === 1 && lines[0]?.own && safeTurns.some((x) => x.analysisStatus === 'pending') && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {t('conversations.runBrandsPendingAnalysis')}
                  </Typography>
                )}
                {lines.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">{t('conversations.runBrandsNone')}</Typography>
                ) : (
                  <>
                    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                      <Box component="thead">
                        <Box component="tr">
                          <Box component="th" sx={HEAD_SX}>{t('conversations.runBrandsColBrand')}</Box>
                          <Box component="th" sx={{ ...HEAD_SX, textAlign: 'right' }}>{t('conversations.runBrandsColPresence')}</Box>
                          <Box component="th" sx={HEAD_SX}>{t('conversations.runBrandsColResult')}</Box>
                        </Box>
                      </Box>
                      <Box component="tbody">
                        {(['own', 'other'] as const).flatMap((group) => {
                          const rows = group === 'own'
                            ? lines.filter((l) => l.own)
                            : (brandsExpanded ? lines.filter((l) => !l.own) : lines.filter((l) => !l.own).slice(0, 10));
                          if (rows.length === 0) return [];
                          return [
                            <Box component="tr" key={`h-${group}`}>
                              <Box component="td" colSpan={3} sx={{ ...HEAD_SX, pt: 1.5, borderBottom: 'none' }}>
                                {group === 'own' ? t('conversations.runBrandsGroupOwn') : t('conversations.runBrandsGroupCompetitors')}
                              </Box>
                            </Box>,
                            ...rows.map((l) => {
                              // Only rows whose NAME resolved to a real domain get a logo —
                              // no placeholder, no slugged domain (competitors.md §447).
                              const resolved = resolvedDomains.get(l.brand.toLowerCase());
                              const result = l.own
                                ? (run && !failed ? squareDesc(run, t) : t('conversations.runsOutcomeFailed'))
                                : l.lastCitedTurn === safeTurns[safeTurns.length - 1]?.turn
                                  ? t('conversations.runsOutcomeKept')
                                  : t('conversations.runsOutcomeDrop', { turn: (l.lastCitedTurn ?? 0) + 1 });
                              return (
                                <Box component="tr" key={`${group}-${l.brand}`}>
                                  <Box component="td" sx={CELL_SX}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                      {resolved && <DomainFavicon domain={resolved.domain} name={resolved.name} size={16} />}
                                      <Typography variant="body2" fontWeight={l.own ? 700 : 400} noWrap>{l.brand}</Typography>
                                      {resolved && (
                                        <Typography variant="caption" color="text.secondary" noWrap>{resolved.domain}</Typography>
                                      )}
                                      {!l.own && !l.tracked && (
                                        <Chip label={t('conversations.runBrandUntrackedTag')} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.625rem' }} />
                                      )}
                                      {!l.own && !l.tracked && (
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          disabled={addedBrands.includes(l.brand)}
                                          onClick={() => handleAddCompetitor(l.brand)}
                                          sx={{ height: 20, fontSize: '0.625rem', px: 1, py: 0, minWidth: 0, lineHeight: 1.6 }}
                                        >
                                          {addedBrands.includes(l.brand) ? t('conversations.annoUntrackedAdded') : t('conversations.runBrandsFollowCta')}
                                        </Button>
                                      )}
                                    </Box>
                                  </Box>
                                  <Box component="td" sx={{ ...CELL_SX, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                    {safeTurns.length > 0 ? `${Math.round((l.turnsCited / safeTurns.length) * 100)} %` : '—'}
                                  </Box>
                                  <Box component="td" sx={CELL_SX}>
                                    <Typography variant="body2" color="text.secondary">{result}</Typography>
                                  </Box>
                                </Box>
                              );
                            }),
                          ];
                        })}
                      </Box>
                    </Box>

                    {lines.filter((l) => !l.own).length > 10 && (
                      <Box sx={{ mt: 1 }}>
                        <Button size="small" variant="text" sx={{ fontSize: '0.75rem', p: 0 }}
                          onClick={() => setBrandsExpanded((v) => !v)}>
                          {brandsExpanded
                            ? t('conversations.runBrandsCollapse')
                            : t('conversations.runBrandsExpand', { n: lines.filter((l) => !l.own).length - 10 })}
                        </Button>
                      </Box>
                    )}

                    <Details summary={t('conversations.runLaneDetails')}>
                      <Box sx={{ overflowX: 'auto' }}>
                        <Box component="table" sx={{ borderCollapse: 'collapse', minWidth: 420 }}>
                          <Box component="thead">
                            {/* Stage grouping row */}
                            <Box component="tr">
                              <Box component="th" sx={HEAD_SX} />
                              {OBSERVED_INTENT_ORDER.map((intent) => {
                                const intentOf2 = (x: ConversationTurn) => {
                                  const raw = (x.observedIntent ?? 'other') as string;
                                  return (OBSERVED_INTENT_ORDER as readonly string[]).includes(raw) ? raw : 'other';
                                };
                                const span = safeTurns.filter((x) => intentOf2(x) === intent).length;
                                if (span === 0) return null;
                                return (
                                  <Box component="th" key={intent} colSpan={span} sx={{ ...HEAD_SX, textAlign: 'center' }}>
                                    {t(`conversations.persona.observedIntent_${intent}`)}
                                  </Box>
                                );
                              })}
                            </Box>
                            <Box component="tr">
                              <Box component="th" sx={HEAD_SX}>{t('conversations.runBrandsColBrand')}</Box>
                              {safeTurns.map((turn) => (
                                <Box
                                  component="th"
                                  key={turn.turn}
                                  sx={{ ...HEAD_SX, textAlign: 'center', ...(dropOut === turn.turn ? { borderLeft: '2px solid', borderRight: '2px solid', borderLeftColor: 'error.main', borderRightColor: 'error.main', color: 'error.main' } : {}) }}
                                >
                                  T{turn.turn}
                                </Box>
                              ))}
                            </Box>
                          </Box>
                          <Box component="tbody">
                            {lines.map((l) => (
                              <Box component="tr" key={`lane-${l.brand}`}>
                                <Box component="td" sx={{ ...CELL_SX, whiteSpace: 'nowrap', fontWeight: l.own ? 700 : 400 }}>{l.brand}</Box>
                                {safeTurns.map((turn) => {
                                  const key = l.own
                                    ? Object.keys(turn.lanes ?? {}).find((k) => k.toLowerCase() === l.brand.toLowerCase())
                                    : l.brand;
                                  const hasLane = key != null && Object.prototype.hasOwnProperty.call(turn.lanes ?? {}, key);
                                  const rank = hasLane ? turn.lanes[key!] : undefined;
                                  const named = l.own
                                    ? turn.cited
                                    : (turn.competitorsMentioned ?? []).includes(l.brand) || (turn.otherBrandsCited ?? []).includes(l.brand) || hasLane;
                                  const text = !named ? '–' : rank == null ? '•' : String(rank);
                                  const title = !named ? t('conversations.transcriptAbsent')
                                    : rank == null ? t('conversations.runLaneUnranked') : t('conversations.runLaneRank', { rank });
                                  return (
                                    <Tooltip key={turn.turn} title={title}>
                                      <Box
                                        component="td"
                                        sx={{
                                          ...CELL_SX, textAlign: 'center', fontVariantNumeric: 'tabular-nums',
                                          color: named ? (l.own ? 'primary.main' : 'text.primary') : 'text.disabled',
                                          fontWeight: named && l.own ? 700 : 400,
                                          ...(dropOut === turn.turn ? { borderLeft: '2px solid', borderRight: '2px solid', borderLeftColor: 'error.main', borderRightColor: 'error.main' } : {}),
                                        }}
                                      >
                                        {text}
                                      </Box>
                                    </Tooltip>
                                  );
                                })}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {t('conversations.runLaneLegend')}
                      </Typography>
                    </Details>
                  </>
                )}
              </Box>

              {/* Cibles */}
              <Box sx={{ display: runLens === 'cibles' ? undefined : 'none' }}>
                <SectionTitle hint={t('conversations.runCiblesHint')}>{t('conversations.runCiblesTitle')}</SectionTitle>
                {cibles.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">{t('conversations.runCiblesNone')}</Typography>
                ) : (
                  <Stack spacing={1}>
                    {cibles.slice(0, 12).map((c) => {
                      const color = CATEGORY_CHIP_COLOR[c.category];
                      return (
                        <Box key={c.targetId} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={t(`cibles.tab.${c.category}`)}
                            size="small"
                            sx={{ height: 20, fontSize: '0.625rem', fontWeight: 700, bgcolor: color?.bg, color: color?.fg }}
                          />
                          <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap title={c.value}>
                            {c.value.split('|').join(' + ')}
                          </Typography>
                          {c.viaSource && (
                            <Chip label={t('conversations.runCibleViaSource')} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.625rem' }} />
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                            {t('conversations.runTurnsCount', { count: c.turns })}
                          </Typography>
                        </Box>
                      );
                    })}
                    {cibles.length > 12 && (
                      <Typography variant="caption" color="text.secondary">
                        {t('conversations.runCiblesMore', { count: cibles.length - 12 })}
                      </Typography>
                    )}
                  </Stack>
                )}
              </Box>

              {/* Sources */}
              <Box sx={{ display: runLens === 'sources' ? undefined : 'none' }}>
                <SectionTitle hint={t('conversations.runSourcesHint')}>
                  {t('conversations.runSourcesTitle', { engine: engineLabel })}
                </SectionTitle>
                  {sources.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">{t('conversations.runSourcesNone')}</Typography>
                  ) : (
                    <SourcesByIntent sources={sources} safeTurns={safeTurns} />
                  )}
                {sources.length > 0 && (
                  <Details summary={t('conversations.runSourcesDetails')}>
                    <Box sx={{ overflowX: 'auto' }}>
                      <Box component="table" sx={{ borderCollapse: 'collapse', minWidth: 480 }}>
                        <Box component="thead">
                          <Box component="tr">
                            <Box component="th" sx={HEAD_SX}>{t('conversations.runSourcesColDomain')}</Box>
                            {safeTurns.map((turn) => (
                              <Box
                                component="th"
                                key={turn.turn}
                                sx={{ ...HEAD_SX, textAlign: 'center', ...(dropOut === turn.turn ? { borderLeft: '2px solid', borderRight: '2px solid', borderLeftColor: 'error.main', borderRightColor: 'error.main', color: 'error.main' } : {}) }}
                              >
                                T{turn.turn}
                              </Box>
                            ))}
                            <Box component="th" sx={{ ...HEAD_SX, textAlign: 'right' }}>{t('conversations.runSourcesColTotal')}</Box>
                          </Box>
                        </Box>
                        <Box component="tbody">
                          {sources.map((s) => (
                            <Box component="tr" key={s.domain}>
                              <Box component="td" sx={{ ...CELL_SX, whiteSpace: 'nowrap', fontWeight: s.own ? 700 : 400, color: s.own ? 'primary.main' : undefined }}>
                                <Box
                                  component="a"
                                  href={s.primaryUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={s.primaryUrl}
                                  sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                >
                                  {truncateUrl(s.primaryUrl)}
                                </Box>
                              </Box>
                              {safeTurns.map((turn) => (
                                <Box
                                  component="td"
                                  key={turn.turn}
                                  sx={{
                                    ...CELL_SX, textAlign: 'center', fontVariantNumeric: 'tabular-nums',
                                    color: s.byTurn.get(turn.turn) ? 'text.primary' : 'text.disabled',
                                    ...(dropOut === turn.turn ? { borderLeft: '2px solid', borderRight: '2px solid', borderLeftColor: 'error.main', borderRightColor: 'error.main' } : {}),
                                  }}
                                >
                                  {s.byTurn.get(turn.turn) ?? '·'}
                                </Box>
                              ))}
                              <Box component="td" sx={{ ...CELL_SX, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{s.cit}</Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                    {extras && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1.25, display: 'block' }}>
                        {t('conversations.runSourcesCellContext', {
                          domains: extras.topDomains?.length ?? 0,
                          runs: cell?.stats?.n ?? 0,
                        })}
                      </Typography>
                    )}
                  </Details>
                )}
              </Box>
            </Panel>
          )}

          {/* ---------- 5. Tour par tour ---------- */}
          {turnsState === 'ready' && safeTurns.length > 0 && (
            <Panel sx={{ mb: 2 }}>
              <SectionTitle hint={t('conversations.runTurnsHint')}>{t('conversations.runTurnsTitle')}</SectionTitle>
              <Details summary={t('conversations.runSignalsDetails')}>
                <Box sx={{ overflowX: 'auto' }}>
                  <Box component="table" sx={{ borderCollapse: 'collapse', minWidth: 520 }}>
                    <Box component="thead">
                      <Box component="tr">
                        <Box component="th" sx={HEAD_SX}>{t('conversations.runSignalsColSignal')}</Box>
                        <Box component="th" sx={HEAD_SX}>{t('conversations.runSignalsColKind')}</Box>
                        {safeTurns.map((turn) => (
                          <Box component="th" key={turn.turn} sx={{ ...HEAD_SX, textAlign: 'center' }}>T{turn.turn}</Box>
                        ))}
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {([
                        ['cited', 'regex', (x: ConversationTurn) => (x.cited ? t('conversations.runYes') : t('conversations.runNo'))],
                        ['citedPosition', 'regex', (x: ConversationTurn) => (x.citedPosition != null ? String(x.citedPosition) : '·')],
                        ['brandIsSource', 'regex', (x: ConversationTurn) => (x.brandIsSource ? t('conversations.runYes') : t('conversations.runNo'))],
                        ['competitorCount', 'regex', (x: ConversationTurn) => String(x.competitorCount)],
                        ['narrativeAlignment', 'llm', (x: ConversationTurn) => (x.narrativeAlignment != null ? String(x.narrativeAlignment) : '·')],
                        ['recommendationStrength', 'llm', (x: ConversationTurn) => x.recommendationStrength ?? '·'],
                        ['sentiment', 'llm', (x: ConversationTurn) => x.sentiment ?? '·'],
                        ['eliminated', 'llm', (x: ConversationTurn) => (x.eliminated == null ? '·' : x.eliminated ? t('conversations.runYes') : t('conversations.runNo'))],
                      ] as [string, 'regex' | 'llm', (x: ConversationTurn) => string][]).map(([key, kind, read]) => (
                        <Box component="tr" key={key}>
                          <Box component="td" sx={{ ...CELL_SX, whiteSpace: 'nowrap' }}>{t(SIGNAL_LABELS[key] ?? key)}</Box>
                          <Box component="td" sx={CELL_SX}>
                            <Chip
                              label={kind === 'regex' ? t('conversations.runSignalKindText') : t('conversations.runSignalKindLlm')}
                              size="small"
                              variant="outlined"
                              sx={{ height: 18, fontSize: '0.625rem' }}
                            />
                          </Box>
                          {safeTurns.map((turn) => (
                            <Box component="td" key={turn.turn} sx={{ ...CELL_SX, textAlign: 'center', fontSize: '0.75rem' }}>
                              {read(turn)}
                            </Box>
                          ))}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {t('conversations.runSignalsLegend')}
                </Typography>
              </Details>
            </Panel>
          )}

          {/* ---------- 6. Lecture de la conversation ---------- */}
          {/* No Panel wrapper: the mock has no outer card/border around the dark phone.
              The SectionTitle floats above; the dark phone chrome is flush on the page. */}
          <Box sx={{ mt: 3.5, mb: 1 }}>
            <SectionTitle hint={t('conversations.transcriptLegend')}>{t('conversations.transcriptTitle')}</SectionTitle>
          </Box>
          {turnsState === 'loading' ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 4, justifyContent: 'center' }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">{t('conversations.transcriptLoading')}</Typography>
            </Box>
          ) : turnsState === 'error' ? (
            <Typography variant="body2" color="text.secondary">{t('conversations.transcriptError')}</Typography>
          ) : (
            <ConversationTranscript
              turns={safeTurns}
              brandId={brandId}
              brandName={brand?.name ?? ''}
              dropOutTurn={dropOut}
              untrackedBrands={untracked}
              addedBrands={addedBrands}
              onAddCompetitor={handleAddCompetitor}
              engine={parsed.engine}
              zoneLabel={zoneLabel}
              countryCode={persona.cells[cellIdx]?.zoneMeta?.countryCode ?? undefined}
              resolvedDomains={resolvedDomains}
            />
          )}

          <Divider sx={{ my: 3 }} />
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/brand/conversations/${personaId}`)}>
            {t('conversations.runBackToPersona')}
          </Button>
        </>
      )}

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>

      {followDialogLabel && brandId && (
        <CompetitorFollowDialog
          open={Boolean(followDialogLabel)}
          label={followDialogLabel}
          sourceDomains={sourceDomains}
          brandId={brandId}
          personaId={personaId}
          launchId={launchId}
          cellPath={cellPath}
          onClose={() => setFollowDialogLabel(null)}
          onSuccess={(label) => {
            setAddedBrands((prev) => [...prev, label]);
            setFollowDialogLabel(null);
            setToast({ message: t('conversations.runToastCompetitorAdded', { brand: label }), severity: 'success' });
          }}
        />
      )}
    </Box>
  );
}
